import type { TeacherDashboardStudent, TeacherDashboardLink } from "../../api/teacherWeb";

const techTypeLabels: Record<string, string> = {
  left_backhand_backspin: "左手反手拉下旋球",
  left_backhand_chop: "左手反手削球",
  left_backhand_defense: "左手反手防守",
  left_backhand_drive: "左手反手攻球",
  left_backhand_push: "左手反手搓球",
  left_backhand_topspin: "左手反手拉上旋球",
  left_forehand_backspin: "左手正手拉下旋球",
  left_forehand_chop: "左手正手削球",
  left_forehand_defense: "左手正手防守",
  left_forehand_drive: "左手正手攻球",
  left_forehand_push: "左手正手搓球",
  left_forehand_topspin: "左手正手拉上旋球",
  right_backhand_backspin: "右手反手拉下旋球",
  right_backhand_chop: "右手反手削球",
  right_backhand_defense: "右手反手防守",
  right_backhand_drive: "右手反手攻球",
  right_backhand_push: "右手反手搓球",
  right_backhand_topspin: "右手反手拉上旋球",
  right_forehand_backspin: "右手正手拉下旋球",
  right_forehand_chop: "右手正手削球",
  right_forehand_defense: "右手正手防守",
  right_forehand_drive: "右手正手攻球",
  right_forehand_push: "右手正手搓球",
  right_forehand_topspin: "右手正手拉上旋球",
};

export const actionTypes = Object.entries(techTypeLabels).filter(([key]) => key.startsWith("right_")).map(([key, label]) => ({ key: key.slice(6), label: label.slice(2) }));
export const actionKey = (tech: string) => tech.replace(/^(left|right)_/, "");
export const actionLabel = (tech: string) => tech === "all" ? "全部动作" : actionTypes.find(action => action.key === actionKey(tech))?.label ?? techLabel(tech);
export function groupedActionItems(counts: Record<string, number>) {
  const grouped = new Map<string, number>();
  Object.entries(counts).forEach(([tech, count]) => {
    const key = actionKey(tech);
    if (actionTypes.some(action => action.key === key) && Number.isFinite(count) && count > 0) grouped.set(key, (grouped.get(key) ?? 0) + count);
  });
  return [...grouped].map(([key, value]) => ({ key, label: actionLabel(key), value })).sort((a, b) => b.value - a.value || a.key.localeCompare(b.key));
}

export const isPhoneInputValid = (value: string) => /^\d{11}$/.test(value.trim());
export const isCodeInputValid = (value: string) => /^\d{6}$/.test(value.trim());
export const techLabel = (value?: string | null) => value ? (techTypeLabels[value] ?? value) : "尚无记录";
export const studentName = (student: { studentName: string | null }) => student.studentName?.trim() || "未填写姓名";
export const studentKey = (student: TeacherDashboardStudent) => `${student.linkId}:${student.studentUserId}`;
export const formatCount = (value: number) => value === -1 ? "不限" : value.toLocaleString("zh-CN");
export function formatBytes(value: number): string {
  if (value === -1) return "不限";
  if (value === 0) return "0 B";
  const unit = Math.min(3, Math.floor(Math.log(Math.max(1, value)) / Math.log(1024)));
  return `${(value / 1024 ** unit).toLocaleString("zh-CN", { maximumFractionDigits: unit ? 2 : 0 })} ${["B", "KiB", "MiB", "GiB"][unit]}`;
}
// Backend naive datetimes are Beijing wall time, regardless of browser timezone.
export function beijingDate(value: string): Date {
  return new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value.replace(" ", "T")}+08:00`);
}
export function formatDate(value?: string | null, withTime = true): string {
  if (!value) return "暂无";
  const date = beijingDate(value);
  if (Number.isNaN(date.getTime())) return "时间未知";
  return date.toLocaleString("zh-CN", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit", ...(withTime ? { hour: "2-digit", minute: "2-digit" } as const : {}) });
}
export function statusInfo(status?: string | null): { label: string; tone: string } {
  if (status === "comp completed") return { label: "已完成", tone: "success" };
  if (status === "comp failed") return { label: "失败", tone: "error" };
  if (status === "started") return { label: "分析中", tone: "running" };
  return { label: status ? "其他状态" : "未提交", tone: "neutral" };
}
export function linkStatus(link: TeacherDashboardLink, now = Date.now()): string {
  if (!link.isActive) return "已停用";
  if (link.expiresAt && beijingDate(link.expiresAt).getTime() <= now) return "已到期";
  return "启用中";
}
export function actionItems(counts: Record<string, number>) {
  return Object.entries(counts).filter(([, value]) => Number.isFinite(value) && value > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, value]) => ({ key, label: techLabel(key), value }));
}
export type StudentFilter = "all" | "running" | "failed" | "unsubmitted";
export type StudentSort = "score-desc" | "score-asc" | "count" | "name";
export function filterStudents(students: TeacherDashboardStudent[], query: string, filter: StudentFilter, sort: StudentSort) {
  const term = query.trim().toLocaleLowerCase();
  const filtered = students.filter(student => {
    const matchesTerm = !term || [studentName(student), student.phoneMasked, student.linkTitle || ""].some(value => value.toLocaleLowerCase().includes(term));
    const matchesStatus = filter === "all" || (filter === "unsubmitted" ? student.runCount === 0 : filter === "failed" ? student.failedCount > 0 : student.latestRunStatus === "started");
    return matchesTerm && matchesStatus;
  });
  return filtered.sort((a, b) => {
    if (sort === "count") return b.runCount - a.runCount || studentKey(a).localeCompare(studentKey(b));
    if (sort === "name") return studentName(a).localeCompare(studentName(b), "zh-CN") || a.linkId - b.linkId;
    return beijingDate(b.latestRunAt || b.boundAt).getTime() - beijingDate(a.latestRunAt || a.boundAt).getTime() || studentKey(a).localeCompare(studentKey(b));
  });
}

export function studentActionCount(student: TeacherDashboardStudent, tech: string) {
  return tech === "all" ? student.runCount : Object.entries(student.actionCounts ?? {}).reduce((sum, [action, count]) => sum + (actionKey(action) === tech ? count : 0), 0);
}
